import {
  type KeenSliderHooks,
  type KeenSliderInstance,
  type KeenSliderPlugin,
} from 'keen-slider/react'

type Timeout = ReturnType<typeof setTimeout>

interface Position {
  x: number
  y: number
}

/**
 * Gets slide count per view from slider options.
 */
export const getSlidesPerView = (
  slider: KeenSliderInstance<unknown, unknown, KeenSliderHooks>,
) => {
  const sliderOptions = slider.options.slides

  if (
    sliderOptions &&
    typeof sliderOptions === 'object' &&
    sliderOptions.perView &&
    typeof sliderOptions.perView === 'number'
  ) {
    return sliderOptions.perView
  }
}

/**
 * Observes changes in child elements inside slider container and updates slider instance.
 */
export const MutationKeenSliderPlugin: KeenSliderPlugin = (slider) => {
  const observer = new MutationObserver((mutations) => {
    mutations.forEach(() => {
      slider.update()
    })
  })
  const config = {
    childList: true,
  }

  slider.on('created', () => {
    observer.observe(slider.container, config)
  })
  slider.on('destroyed', () => {
    observer.disconnect()
  })
}

/**
 * Observes changes in slider container size and updates slider instance.
 */
export const ResizeKeenSliderPlugin: KeenSliderPlugin = (slider) => {
  const observer = new ResizeObserver(function () {
    slider.update()
  })

  slider.on('created', () => {
    observer.observe(slider.container)
  })
  slider.on('destroyed', () => {
    observer.unobserve(slider.container)
  })
}

/**
 * Handle wheel scroll in slider.
 */
export const WheelControlsKeenSliderPlugin: KeenSliderPlugin = (slider) => {
  let touchTimeout: Timeout
  let position: Position
  let wheelActive: boolean

  function dispatch(event: WheelEvent, name: string) {
    position.x -= event.deltaX
    position.y -= event.deltaY
    slider.container.dispatchEvent(
      new CustomEvent(name, {
        detail: {
          x: position.x,
          y: position.y,
        },
      }),
    )
  }

  function wheelStart(event: WheelEvent) {
    position = {
      x: event.pageX,
      y: event.pageY,
    }
    dispatch(event, 'ksDragStart')
  }

  function wheel(event: WheelEvent) {
    dispatch(event, 'ksDrag')
  }

  function wheelEnd(event: WheelEvent) {
    dispatch(event, 'ksDragEnd')
  }

  function eventWheel(event: WheelEvent) {
    event.preventDefault()
    if (!wheelActive) {
      wheelStart(event)
      wheelActive = true
    }

    wheel(event)
    clearTimeout(touchTimeout)

    touchTimeout = setTimeout(() => {
      wheelActive = false
      wheelEnd(event)
    }, 50)
  }

  slider.on('created', () => {
    slider.container.addEventListener('wheel', eventWheel, {
      passive: false,
    })
  })
}

/**
 * Automatically rotates slides in slider.
 */
export const AutoSwitchKeenSliderPlugin = (delay: number): KeenSliderPlugin => {
  return (slider) => {
    let timeout: Timeout | undefined = undefined
    let mouseOver = false

    const clearNextTimeout = () => {
      clearTimeout(timeout)
    }

    const nextTimeout = () => {
      clearTimeout(timeout)

      if (mouseOver) {
        return
      }

      timeout = setTimeout(() => {
        slider.next()
      }, delay)
    }

    slider.on('created', () => {
      slider.container.addEventListener('mouseover', () => {
        mouseOver = true

        clearNextTimeout()
      })
      slider.container.addEventListener('mouseout', () => {
        mouseOver = false

        nextTimeout()
      })

      nextTimeout()
    })

    slider.on('dragStarted', clearNextTimeout)
    slider.on('animationEnded', nextTimeout)
    slider.on('updated', nextTimeout)
  }
}
